import { ArrowLeft, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

const aiTermsJa = [
  // ── 基本概念 ──────────────────────────────────────────────────
  {
    id: 1,
    term: "人工知能（AI）",
    category: "概念",
    definition: "人間の知的な活動を模倣するコンピューターシステムの総称です。学習、推論、問題解決などの能力を持ちます。",
    businessApplication: "自動化、意思決定支援、予測分析、顧客サービス向上など、あらゆる業務領域での活用が可能です。"
  },
  {
    id: 2,
    term: "生成AI",
    category: "技術",
    definition: "テキスト、画像、音声、動画などの新しいコンテンツを生成することができるAI技術です。ChatGPTやClaudeがその代表例。",
    businessApplication: "マーケティング素材作成、商品説明文生成、プレゼン資料作成、デザイン制作などに活用できます。"
  },
  {
    id: 3,
    term: "LLM（大規模言語モデル）",
    category: "技術",
    definition: "Large Language Modelの略。膨大なテキストデータで訓練された大規模なAI言語モデル。GPT-4、Claude、Geminiなどが該当します。",
    businessApplication: "高度な文章生成、複雑な質問応答、創作活動、専門的な文書作成支援などに活用されます。"
  },
  {
    id: 4,
    term: "機械学習",
    category: "技術",
    definition: "コンピューターがデータから自動的にパターンを学習し、予測や判断を行う技術です。現代のAIの中核を担います。",
    businessApplication: "売上予測、顧客行動分析、不正検知、商品推薦システムなどに活用されています。"
  },
  {
    id: 5,
    term: "自然言語処理（NLP）",
    category: "技術",
    definition: "人間が日常的に使っている言語をコンピューターに理解・処理させる技術です。ChatGPTなどのAIチャットの基盤。",
    businessApplication: "文書分析、感情分析、翻訳、チャットボット、音声認識などに使用されます。"
  },
  {
    id: 6,
    term: "ディープラーニング",
    category: "技術",
    definition: "人間の脳の神経回路を模倣したニューラルネットワークを多層化した機械学習の手法。画像認識や音声認識の精度を革命的に高めました。",
    businessApplication: "画像認識、音声認識、需要予測、異常検知など、複雑なパターン認識が必要な業務に活用されます。"
  },
  {
    id: 7,
    term: "マルチモーダルAI",
    category: "技術",
    definition: "テキスト、画像、音声、動画など複数の形式のデータを同時に処理できるAI技術。最新のGPT-4o、Claude、Geminiが対応。",
    businessApplication: "画像付き資料の分析、動画内容の理解、製品写真からの説明文自動生成などに使用されます。"
  },
  // ── プロンプト・操作 ────────────────────────────────────────────
  {
    id: 8,
    term: "プロンプト",
    category: "用語",
    definition: "AIに対する指示や質問のこと。効果的なプロンプトを作成することで、より良い結果を得ることができます。",
    businessApplication: "AIツールを効果的に活用するための重要なスキル。具体的で明確な指示により業務効率が向上します。"
  },
  {
    id: 9,
    term: "プロンプトエンジニアリング",
    category: "技術",
    definition: "AIから最適な回答を得るために、効果的な指示文（プロンプト）を設計・最適化する技術です。",
    businessApplication: "AI活用の効率化、一貫した品質の出力獲得、業務プロセスの標準化、AIツールの効果最大化に重要です。"
  },
  {
    id: 10,
    term: "プロンプトテンプレート",
    category: "用語",
    definition: "繰り返し使用できるよう標準化されたAIへの指示文。変数を埋め込んで再利用できます。",
    businessApplication: "業務の標準化、品質の一貫性確保、チーム内でのベストプラクティス共有、作業効率の向上に活用されます。"
  },
  {
    id: 11,
    term: "コンテキストウィンドウ",
    category: "用語",
    definition: "AIが一度に処理できるテキストの長さの上限。最新モデルは100万〜200万トークンまで対応し、書籍1冊分以上を一括処理できます。",
    businessApplication: "長文書の分析、大量データの一括処理、複雑な指示の実行、詳細な分析レポート作成に影響します。"
  },
  {
    id: 12,
    term: "トークン",
    category: "用語",
    definition: "AIが処理するテキストの最小単位。日本語は1文字が約1〜2トークンに相当します。料金計算や処理速度に影響します。",
    businessApplication: "AI利用コストの計算、処理効率の最適化、バッチ処理の計画、予算管理に重要な要素です。"
  },
  {
    id: 13,
    term: "温度パラメータ",
    category: "用語",
    definition: "AIの出力の創造性やランダム性を制御するパラメータ。低いほど一貫性が高く、高いほど創造的になります。",
    businessApplication: "用途に応じたAI出力の調整、創造的タスクと分析的タスクでの最適化、品質の安定性確保に使用されます。"
  },
  {
    id: 14,
    term: "ハルシネーション",
    category: "用語",
    definition: "AIが事実に基づかない情報や存在しない内容を生成してしまう現象。「AIの嘘」とも呼ばれます。",
    businessApplication: "AI出力の品質管理、事実確認プロセスの重要性、信頼性の高いAI活用のためのガイドライン策定に関連します。"
  },
  {
    id: 15,
    term: "ストリーミング出力",
    category: "用語",
    definition: "AIが回答を生成しながらリアルタイムで表示する機能。全体の完成を待たずに結果を確認できます。",
    businessApplication: "ユーザー体験の向上、長文生成時の待ち時間削減、リアルタイムでの内容確認と修正指示に役立ちます。"
  },
  {
    id: 16,
    term: "モデル選択",
    category: "用語",
    definition: "タスクの性質、コスト、速度、品質などを考慮して最適なAIモデルを選ぶことです。",
    businessApplication: "予算管理、処理速度の最適化、品質要件の達成。用途に応じてGPT-4、Claude、Geminiなどを使い分けます。"
  },
  {
    id: 17,
    term: "セルフホスト",
    category: "用語",
    definition: "クラウドサービスを使わず、自社のサーバーやインフラでAIやツールを運用する形態です。",
    businessApplication: "データの完全管理、コスト最適化、カスタマイズの自由度向上、コンプライアンス要件への対応に使用されます。"
  },
  // ── 技術・アーキテクチャ ────────────────────────────────────────
  {
    id: 18,
    term: "API",
    category: "技術",
    definition: "Application Programming Interfaceの略。異なるソフトウェア同士が情報をやり取りするための仕組みです。AIを自社システムに組み込む際に必須。",
    businessApplication: "既存システムにAI機能を組み込んだり、複数のツールを連携させて業務効率化を図ることができます。"
  },
  {
    id: 19,
    term: "RAG（検索拡張生成）",
    category: "技術",
    definition: "Retrieval-Augmented Generationの略。外部データベースから関連情報を検索してAIの回答精度を向上させる技術です。社内文書をAIに読み込ませる仕組みの核心。",
    businessApplication: "企業ナレッジベースの活用、顧客サポート、法務文書検索、技術文書の質問応答システムに使用されます。"
  },
  {
    id: 20,
    term: "AIエージェント",
    category: "技術",
    definition: "人間の指示を受けて自律的にタスクを計画・実行するAIシステム。ブラウザ操作やファイル編集など複数のツールを使い分けて目標を達成します。",
    businessApplication: "予約代行、データ収集、レポート作成、コード開発など、複数ステップの業務を自動化できます。"
  },
  {
    id: 21,
    term: "マルチエージェントシステム",
    category: "技術",
    definition: "複数のAIエージェントが協調して複雑なタスクを分担・実行する仕組み。調査担当・実装担当・レビュー担当などが並列で動作します。",
    businessApplication: "大規模なコード開発、包括的な市場調査、複雑な業務フローの完全自動化などに活用されます。"
  },
  {
    id: 22,
    term: "AIエージェント自律実行",
    category: "技術",
    definition: "人間の監督なしにAIが計画→ツール選択→実行→検証を繰り返す能力。Claude CodeやDevinが代表例。",
    businessApplication: "長時間の反復作業、夜間バッチ処理、継続的なデータ更新など、人手を介さない自動化に活用されます。"
  },
  {
    id: 23,
    term: "MCP（Model Context Protocol）",
    category: "技術",
    definition: "AIモデルが外部データソースやツールと標準化された方法で連携するためのプロトコル。Anthropicが策定し業界標準になりつつあります。",
    businessApplication: "Google Drive、Slack、データベースなど複数システムをAIに接続し、統合的な業務支援を実現します。"
  },
  {
    id: 24,
    term: "Function Calling（ツール呼び出し）",
    category: "技術",
    definition: "AIが外部のツールやAPIを自律的に呼び出して情報を取得したり、アクションを実行する機能。AIエージェントの核心技術。",
    businessApplication: "カレンダー予約、データベース検索、計算実行、外部サービス連携など、実世界のタスク実行に使用されます。"
  },
  {
    id: 25,
    term: "ベクトルデータベース",
    category: "技術",
    definition: "テキストや画像を数値ベクトルとして保存・検索するためのデータベース。RAGシステムで社内文書を検索可能にする基盤。",
    businessApplication: "社内文書検索、類似商品推薦、FAQシステム、ナレッジベース構築などRAGシステムの基盤として使用されます。"
  },
  {
    id: 26,
    term: "ファインチューニング",
    category: "技術",
    definition: "既存の訓練済みAIモデルを特定のタスクやドメインに合わせて追加学習させる手法です。",
    businessApplication: "業界特化型AIの開発、企業固有のデータでのAI最適化、カスタマイズされたソリューション構築に使用されます。"
  },
  {
    id: 27,
    term: "コンテキストキャッシュ",
    category: "技術",
    definition: "同じコンテキスト情報を再利用することでAIの処理を高速化・低コスト化する技術。同じ文書を繰り返し送る際にコストを大幅削減。",
    businessApplication: "大量の定型処理、FAQ対応、繰り返しタスクのコスト削減と応答速度向上に有効です。"
  },
  {
    id: 28,
    term: "推論（Inference）",
    category: "技術",
    definition: "訓練済みのAIモデルが実際にデータを処理して結果を出力するプロセスです。",
    businessApplication: "AIサービスの応答時間、コスト計算、リソース計画に影響。リアルタイム処理が必要な業務では特に重要です。"
  },
  {
    id: 29,
    term: "モデルルーティング",
    category: "技術",
    definition: "タスクの種類・コスト・速度に応じてLLMを自動切替えする仕組み。簡単な質問にはHaiku、高度な分析にはOpusを使い分けるなど。",
    businessApplication: "AIコストの最適化、応答速度の改善、品質と効率のバランス調整に活用されます。"
  },
  {
    id: 30,
    term: "音声認識",
    category: "技術",
    definition: "人間の音声を文字データに変換するAI技術。Whisper（OpenAI）などが高精度を実現しています。",
    businessApplication: "音声アシスタント、議事録自動作成、コールセンター業務支援、アクセシビリティ向上に活用されます。"
  },
  {
    id: 31,
    term: "ワークフロー自動化",
    category: "技術",
    definition: "定型的な業務プロセスをAIやツールを使って自動的に実行する仕組み。n8n、Make、Zapierなどで構築します。",
    businessApplication: "データ入力、ファイル整理、通知送信、承認フロー、レポート生成など、定型業務の効率化に活用されます。"
  },
  {
    id: 32,
    term: "ノーコード/ローコード",
    category: "用語",
    definition: "プログラミングなし（またはほぼなし）でアプリやAIワークフローを構築できる開発手法です。",
    businessApplication: "非エンジニアでもAIツール連携やチャットボット作成が可能。DX推進や業務改善の内製化に有効です。"
  },
  // ── 2025-2026年 最新トレンド ───────────────────────────────────
  {
    id: 33,
    term: "AI駆動開発（AIDD）",
    category: "技術",
    definition: "AIをコーディングの主役に据えた開発スタイル。人間が設計・レビューしAIが実装する手法。Claude CodeやCursorなどで急速に普及中。",
    businessApplication: "開発コストと時間の大幅削減、少人数チームでの高品質なプロダクト開発、非エンジニアによるアプリ制作が可能に。"
  },
  {
    id: 34,
    term: "バイブコーディング（Vibe Coding）",
    category: "技術",
    definition: "自然言語の指示だけでAIにコードを書かせる開発スタイル。コードの詳細を読まなくても開発できる。Andrej Karpathy（元OpenAI）が提唱。",
    businessApplication: "プロトタイプの爆速制作、非エンジニアによる社内ツール開発、アイデアを即座に形にする用途に活用されます。"
  },
  {
    id: 35,
    term: "コンテキスト長競争",
    category: "概念",
    definition: "LLMが処理できる文脈の上限を各社が競って拡大中の動向。Claude=200万トークン、Gemini=200万トークンなど、書籍数冊分を一括処理できるようになっています。",
    businessApplication: "大量の社内文書・契約書・データを一括でAIに渡せるようになり、包括的な分析・検索が可能になります。"
  },
  {
    id: 36,
    term: "エージェント協調（Multi-Agent Collaboration）",
    category: "技術",
    definition: "複数の専門AIエージェントが役割分担し複雑問題を解決する手法。調査エージェント・実装エージェント・テストエージェントなどが並列動作。",
    businessApplication: "大規模なシステム開発、包括的なリサーチレポート作成、複数部署にまたがる業務の一括自動化などに活用されます。"
  },
  {
    id: 37,
    term: "AIネイティブ",
    category: "概念",
    definition: "AIを後付けで導入するのではなく、AIを前提として最初から設計・構築されたサービスや組織のあり方です。",
    businessApplication: "競合に対する圧倒的な生産性優位、人件費構造の変革、AIと人間の最適な役割分担による組織設計に関連します。"
  },
  {
    id: 38,
    term: "人間-AIコラボレーション",
    category: "概念",
    definition: "人間の判断力・創造性・倫理観とAIの処理速度・知識量を組み合わせた作業形態。AIを「ツール」ではなく「共同作業者」として扱います。",
    businessApplication: "複雑な意思決定の品質向上、クリエイティブ業務の効率化、AIの強みと人間の強みを補完し合う業務設計に活用されます。"
  },
  {
    id: 39,
    term: "AGI（汎用人工知能）",
    category: "概念",
    definition: "人間と同等以上のあらゆる知的タスクをこなせるAI。OpenAI・Anthropic・GoogleなどのAI企業が開発を競っています。",
    businessApplication: "現在はまだ実現していませんが、AGI到来を見据えた業務設計・スキル習得の戦略立案が経営課題になっています。"
  },
  {
    id: 40,
    term: "AI倫理",
    category: "概念",
    definition: "AIの開発・利用における公正性、透明性、プライバシー、責任などの倫理的原則です。",
    businessApplication: "採用AI、与信判断、顧客対応など、人に影響を与える業務でのAI利用における指針として重要です。"
  },
  {
    id: 41,
    term: "バイアス",
    category: "概念",
    definition: "AIシステムが特定の傾向や偏見を持つこと。訓練データの偏りや設計上の問題から生じます。",
    businessApplication: "公平な採用システム、金融サービス、医療診断など、倫理的なAI活用において重要な考慮事項です。"
  },
  // ── ツール・サービス名 ──────────────────────────────────────────
  {
    id: 42,
    term: "Claude Code",
    category: "ツール名",
    definition: "Anthropic製のAIコーディングCLI（コマンドラインツール）。ターミナルからプロジェクト全体を把握し、複数ファイルの同時編集・Git操作・テスト実行を自律実行するAIエンジニア。",
    businessApplication: "エンジニアの開発速度を数倍に向上。コードレビュー、バグ修正、機能追加をAIに指示するだけで完了します。"
  },
  {
    id: 43,
    term: "Claude Fable",
    category: "ツール名",
    definition: "Anthropicの最上位AIモデル（モデルID: claude-fable-5）。複雑な推論・長期自律タスク・高精度な分析に特化した最高品質のモデルです。",
    businessApplication: "高度な法律文書の分析、複雑な事業戦略の立案補助、長時間の自律的なリサーチタスクなどに活用されます。"
  },
  {
    id: 44,
    term: "Claude APIモデル群",
    category: "ツール名",
    definition: "AnthropicのAPI経由で利用できるモデル群。Opus 4.8（最高品質）/ Sonnet 4.6（バランス型）/ Haiku 4.5（高速・低コスト）の3層構成。",
    businessApplication: "用途とコストに応じたモデル選択が可能。大量処理にはHaiku、高精度な分析にはOpusを使い分けることでコストを最適化できます。"
  },
  {
    id: 45,
    term: "o3/o4（OpenAI推論モデル）",
    category: "ツール名",
    definition: "OpenAIの高度推論シリーズ。数学・科学・コーディングで人間専門家レベルに到達。ChatGPT Pro/Plus経由またはAPIで利用可能。",
    businessApplication: "複雑な数学的分析、高度なコードレビュー、科学的な問題解決など、深い推論が必要な高難度タスクに活用されます。"
  },
  {
    id: 46,
    term: "Gemini 2.5 Pro/Flash",
    category: "ツール名",
    definition: "Googleの最新世代LLM。Pro=高精度・長文対応（200万トークン）、Flash=高速・低コスト。Google WorkspaceやAI Studioから利用可能。",
    businessApplication: "大量ドキュメントの一括分析、Googleサービスとの連携強化、コスト効率の高いAPI利用などに活用されます。"
  },
  {
    id: 47,
    term: "DeepSeek R1",
    category: "ツール名",
    definition: "中国DeepSeek社が開発した推論特化LLM。オープンソース版あり、低コストで高性能。⚠️中国系サービスのため機密情報の入力は避けること。",
    businessApplication: "コスト効率の高いAI活用、ローカル実行による情報漏洩リスク低減。ただし機密情報・個人情報の入力には注意が必要です。"
  },
  {
    id: 48,
    term: "RPA",
    category: "技術",
    definition: "Robotic Process Automationの略。ルーチンワークを自動化するソフトウェアロボット技術。最近はAIと組み合わせてより高度な自動化が可能になっています。",
    businessApplication: "データ入力、帳票処理、定型的な業務プロセスの自動化により、人的コストを削減できます。"
  },
  {
    id: 49,
    term: "並列エージェント実行",
    category: "技術",
    definition: "複数のAIエージェントを同時に動かし、それぞれが独立したサブタスクを処理する手法。順番待ちなしに複数の作業を同時進行できます。",
    businessApplication: "大規模なコードレビュー・リサーチ・テスト実行などを分割して並列処理することで、全体の所要時間を大幅に短縮できます。"
  },
  {
    id: 50,
    term: "仕様駆動開発（Spec-Driven Development）",
    category: "技術",
    definition: "実装前に仕様書・要件定義を厳密に記述し、それをAIや開発者が忠実に実装する開発手法。曖昧さをなくして手戻りを防ぎます。",
    businessApplication: "AIコーディングツールに仕様書を読み込ませることで、意図通りの実装を得やすくなります。チームでの認識合わせにも効果的。"
  },
  {
    id: 51,
    term: "テスト駆動開発（TDD）",
    category: "技術",
    definition: "Test-Driven Developmentの略。コードを書く前にテストを先に書き、テストが通るように実装を進める手法。AIコーディングと組み合わせると品質が向上します。",
    businessApplication: "AIエージェントにTDDを指示することで、バグの少ない堅牢なコードを自動生成できます。PoW Loop（品質ゲート）と組み合わせて使われます。"
  },
  {
    id: 52,
    term: "スコープ（Scope）",
    category: "用語",
    definition: "タスクや機能の実装範囲のこと。AI駆動開発では「どこまでやるか」を明確に定義しないと、AIが余計な変更を加えるリスクがあります。",
    businessApplication: "AIへの指示時にスコープを明示することで、意図しない変更を防止し、レビューコストを下げられます。変更ファイル数・対象モジュールの事前宣言が鉄則。"
  },
  {
    id: 53,
    term: "イテレーション（Iteration）",
    category: "用語",
    definition: "短いサイクルで「実装→テスト→フィードバック→改善」を繰り返す開発手法の一単位。スプリントとも呼ばれます。",
    businessApplication: "AIとのコーディングでは1イテレーション＝1機能単位が基本。小さく区切ることでAIの誤りを早期発見・修正できます。"
  },
  {
    id: 54,
    term: "PoC（概念実証）",
    category: "用語",
    definition: "Proof of Conceptの略。アイデアや技術が実現可能かを小さなスケールで確認する試作フェーズ。AI活用の初期段階でよく使われます。",
    businessApplication: "新しいAI機能の導入前にPoCで効果を検証し、本番導入のリスクを最小化できます。「PoCで終わらせない」ことが実用化の課題です。"
  },
  {
    id: 55,
    term: "リファクタリング",
    category: "技術",
    definition: "外部から見た動作を変えずにコードの内部構造を改善すること。AIはリファクタリングを自動実行できますが、スコープ外への影響に注意が必要です。",
    businessApplication: "技術的負債の解消、コードの可読性向上、保守コスト削減に活用。AIに依頼する際は対象ファイルと変更しない機能を明示することが重要です。"
  },
  {
    id: 56,
    term: "CI/CD（継続的インテグレーション/デリバリー）",
    category: "技術",
    definition: "コードの変更を自動でテスト・ビルド・デプロイする仕組み。AIが書いたコードを自動で品質チェックし、本番環境に反映するパイプラインです。",
    businessApplication: "AI生成コードの品質保証に不可欠。GitHub ActionsやVercelと組み合わせることで、AIが実装→自動テスト→自動デプロイの一連の流れを実現できます。"
  },
  // ── AIエージェント活用の実務用語 ────────────────────────────────
  {
    id: 57,
    term: "パス（Path）",
    category: "エージェント",
    definition: "AIエージェントが読み書きするファイルやディレクトリの場所を示す文字列。どのパスに触れてよいかを許可リスト（allowlist）で制限するのがエージェント運用の基本です。",
    businessApplication: "エージェントの作業範囲をパス単位で限定することで、意図しないファイル変更や情報漏洩を防止。安全な自動化の第一歩です。"
  },
  {
    id: 58,
    term: "ドメイン（Domain）",
    category: "エージェント",
    definition: "「対象業務の領域」と「ネットワーク上の識別名（example.com 等）」の二つの意味で使われます。エージェント運用ではアクセス先ドメインを限定して外部通信を制御します。",
    businessApplication: "通信先ドメインを許可制にすることで機密データの外部送信リスクを抑制。業務領域（ドメイン）ごとに専門エージェントを分ける設計も有効です。"
  },
  {
    id: 59,
    term: "レイヤー（Layer）",
    category: "エージェント",
    definition: "システムを役割ごとに階層化した各層のこと（例：UI層・ロジック層・データ層）。AIに変更を依頼する際、どの層を触るかを明確にすると安全性が高まります。",
    businessApplication: "レイヤーを意識した指示によりAIの変更が他機能へ波及するのを防止。責務分離された設計は保守コスト削減にもつながります。"
  },
  {
    id: 60,
    term: "サブエージェント（Sub-agent）",
    category: "エージェント",
    definition: "メインのAIエージェントが特定の作業を切り出して委譲する下位のエージェント。調査・実装・レビューなどを専門のサブエージェントに並行して任せられます。",
    businessApplication: "重い作業をサブエージェントへ委譲することでメインは司令塔に専念。並行処理で大規模タスクの所要時間を短縮できます。"
  },
  {
    id: 61,
    term: "オーケストレーション（Orchestration）",
    category: "エージェント",
    definition: "複数のAIエージェントやツールの実行順序・連携を統括的に制御すること。誰が何をいつ実行するかを調整する「指揮者」の役割です。",
    businessApplication: "複雑な業務フローを複数エージェントの協調で自動化。人手による進行管理を減らし、大規模な自動化を実現します。"
  },
  {
    id: 62,
    term: "ワークツリー（Worktree）",
    category: "エージェント",
    definition: "一つのGitリポジトリから複数の作業ディレクトリを切り出す仕組み。複数のAIエージェントが互いに干渉せず並行作業するための基盤です。",
    businessApplication: "複数のエージェントが同時に別々の機能を開発しても衝突しないため、並列開発のスループットが向上します。"
  },
];

const aiTermsEn = [
  {
    id: 1,
    term: "Artificial Intelligence (AI)",
    category: "Concept",
    definition: "A general term for computer systems that mimic human intellectual activities. They possess capabilities such as learning, reasoning, and problem-solving.",
    businessApplication: "Applicable across all business domains, including automation, decision support, predictive analytics, and customer service improvement."
  },
  {
    id: 2,
    term: "Generative AI",
    category: "Technology",
    definition: "AI technology capable of generating new content such as text, images, audio, and video. Representative examples include ChatGPT and Claude.",
    businessApplication: "Can be used for creating marketing materials, generating product descriptions, creating presentation materials, and design production."
  },
  {
    id: 3,
    term: "LLM (Large Language Model)",
    category: "Technology",
    definition: "Abbreviation for Large Language Model. Large-scale AI language models trained on massive text data. Includes GPT-4, Claude, and Gemini.",
    businessApplication: "Used for advanced text generation, complex question answering, creative activities, and specialized document creation support."
  },
  {
    id: 4,
    term: "Machine Learning",
    category: "Technology",
    definition: "Technology that allows computers to automatically learn patterns from data and make predictions and judgments. It forms the core of modern AI.",
    businessApplication: "Used for sales forecasting, customer behavior analysis, fraud detection, and product recommendation systems."
  },
  {
    id: 5,
    term: "Natural Language Processing (NLP)",
    category: "Technology",
    definition: "Technology that allows computers to understand and process the language people use in daily life. It is the foundation of AI chats like ChatGPT.",
    businessApplication: "Used for document analysis, sentiment analysis, translation, chatbots, and speech recognition."
  },
  {
    id: 6,
    term: "Deep Learning",
    category: "Technology",
    definition: "A machine learning method that uses multi-layered neural networks modeled after the neural circuits of the human brain. It has revolutionarily improved accuracy in image recognition and speech recognition.",
    businessApplication: "Used in complex pattern recognition tasks such as image recognition, speech recognition, demand forecasting, and anomaly detection."
  },
  {
    id: 7,
    term: "Prompt Engineering",
    category: "Practice",
    definition: "The skill of crafting inputs (prompts) to AI to get the desired output. Appropriate question design dramatically changes AI performance.",
    businessApplication: "Key skill for effectively utilizing AI in all business tasks. Proper prompts can dramatically improve operational efficiency."
  },
  {
    id: 8,
    term: "RAG (Retrieval-Augmented Generation)",
    category: "Technology",
    definition: "A technology that retrieves relevant documents from an external database and uses them as context to answer questions. AI can respond with the latest information beyond its training data.",
    businessApplication: "Enables AI to answer based on internal documents and the latest information, useful for building internal chatbots and inquiry response systems."
  },
  {
    id: 9,
    term: "Fine-tuning",
    category: "Technology",
    definition: "A technique that takes a pre-trained general-purpose AI model and additionally trains it using company-specific or industry-specific data.",
    businessApplication: "Useful for building AI models specialized for specific business fields or company-specific tasks."
  },
  {
    id: 10,
    term: "AI Agent",
    category: "Application",
    definition: "AI that autonomously plans and executes complex tasks over multiple steps. It can use tools, search the web, and execute code.",
    businessApplication: "Automation of complex workflows such as market research, report generation, and data analysis. Enables unattended operation."
  },
  {
    id: 11,
    term: "Multimodal AI",
    category: "Technology",
    definition: "AI that can process multiple types of information including text, images, audio, and video simultaneously.",
    businessApplication: "Used for analyzing product images, identifying problems from customer photos, and generating documents with diagrams."
  },
  {
    id: 12,
    term: "Transformer",
    category: "Technology",
    definition: "A neural network architecture announced by Google in 2017 that became the foundation of modern AI. It specializes in processing sequential data and is the foundation of LLMs.",
    businessApplication: "Incorporated into virtually all AI tools used in business, including ChatGPT and Claude."
  },
  {
    id: 13,
    term: "Vector Database",
    category: "Infrastructure",
    definition: "A database that stores data as numerical vectors (embeddings) and enables fast similarity search. Essential for RAG construction.",
    businessApplication: "Used for semantic search of internal documents, similar example retrieval, and knowledge base construction."
  },
  {
    id: 14,
    term: "Embedding",
    category: "Technology",
    definition: "A technique to convert text, images, etc. into numerical vectors. Words with similar meanings have similar vector values.",
    businessApplication: "Used for automatic document classification, similar text search, and sentiment analysis."
  },
  {
    id: 15,
    term: "Token",
    category: "Concept",
    definition: "The unit in which AI processes text. One token is roughly 0.5–1.5 characters in English. It determines API usage fees.",
    businessApplication: "Important for cost management. AI with more processing capacity can be used by reducing prompt token count."
  },
  {
    id: 16,
    term: "Context Window",
    category: "Concept",
    definition: "The maximum amount of text that AI can process at once. The larger the context window, the longer text and conversations the AI can understand.",
    businessApplication: "Affects the ability to read long documents and long chat history. Important for selecting AI tools."
  },
  {
    id: 17,
    term: "Hallucination",
    category: "Concept",
    definition: "A phenomenon where AI generates factually incorrect or false information while presenting it as true. Also called confabulation.",
    businessApplication: "Requires care when using for medical, legal, and financial fields. Fact-checking with RAG and other technologies is important."
  },
  {
    id: 18,
    term: "Zero-shot Learning",
    category: "Technology",
    definition: "The ability to respond appropriately without any examples. Modern LLMs have this capability.",
    businessApplication: "Makes it possible to solve new problems without creating training data. Enables rapid deployment across various business scenes."
  },
  {
    id: 19,
    term: "Few-shot Learning",
    category: "Technology",
    definition: "A method of teaching AI response patterns using only a few examples. By providing 2–5 examples in the prompt, output accuracy dramatically improves.",
    businessApplication: "Effective for tasks requiring consistent output format, such as report creation and data extraction."
  },
  {
    id: 20,
    term: "Chain of Thought",
    category: "Practice",
    definition: "A technique of instructing AI to think step-by-step. By showing the thought process, accuracy on complex problems improves.",
    businessApplication: "Effective for complex judgment tasks such as contract review, financial analysis, and strategy formulation."
  },
  {
    id: 21,
    term: "AI Hallucination Countermeasures",
    category: "Practice",
    definition: "Methods and systems to reduce false information generated by AI. Specific approaches include RAG and fact-checking.",
    businessApplication: "A must-consider risk management factor when deploying AI in business operations, especially in high-stakes domains."
  },
  {
    id: 22,
    term: "Parameter",
    category: "Technology",
    definition: "The internal values that determine AI model performance. Models with more parameters (e.g., 100 billion) have stronger capabilities but also higher costs.",
    businessApplication: "Basis for AI selection. Large models for complex tasks, small models for simple tasks leads to cost optimization."
  },
  {
    id: 23,
    term: "Inference",
    category: "Technology",
    definition: "The process in which a trained AI model receives new inputs and generates outputs (predictions). What happens when using AI in business.",
    businessApplication: "Speed and cost of AI tools depend on inference performance. An important factor when considering real-time business applications."
  },
  {
    id: 24,
    term: "Model Distillation",
    category: "Technology",
    definition: "A technique to create a smaller model with similar performance from a large high-performance model. Achieves both low cost and high performance.",
    businessApplication: "Useful when deploying AI on smartphones or edge devices. Enables AI operation without internet connection."
  },
  {
    id: 25,
    term: "RLHF (Reinforcement Learning from Human Feedback)",
    category: "Technology",
    definition: "A training method where AI learns to generate outputs that match human preferences. Used in ChatGPT and Claude.",
    businessApplication: "Ensures AI generates responses aligned with business values and policies. Basis for building responsible AI."
  },
  {
    id: 26,
    term: "Responsible AI",
    category: "Concept",
    definition: "The concept of ensuring that AI systems are fair, transparent, and ethical. Important for maintaining trust in AI deployed in business.",
    businessApplication: "A prerequisite for AI governance. Important for complying with regulations and building customer trust."
  },
  {
    id: 27,
    term: "Edge AI",
    category: "Application",
    definition: "AI technology that processes data locally on devices like smartphones and security cameras without sending it to the cloud.",
    businessApplication: "Enables real-time quality inspection at manufacturing sites, offline store analytics, and fast response systems."
  },
  {
    id: 28,
    term: "Federated Learning",
    category: "Technology",
    definition: "A machine learning method that trains models on distributed data without collecting it centrally. Enables AI learning while protecting privacy.",
    businessApplication: "Enables joint AI development between companies and hospitals while protecting personal information. Useful for competitive/confidential data handling."
  },
  {
    id: 29,
    term: "AI Governance",
    category: "Concept",
    definition: "Frameworks, policies, and processes for managing AI deployment and usage. Covers risk management, compliance, and ethical considerations.",
    businessApplication: "Essential for companies deploying AI. Directly connected to legal compliance and reputation management."
  },
  {
    id: 30,
    term: "MLOps",
    category: "Practice",
    definition: "A methodology for efficiently developing, deploying, monitoring, and improving AI/ML models. Bridges machine learning and operations.",
    businessApplication: "Important for companies continuously improving AI systems. Enables rapid model update cycles and quality maintenance."
  },
  {
    id: 31,
    term: "Diffusion Model",
    category: "Technology",
    definition: "The AI technology behind image generation tools like Stable Diffusion and DALL-E. Generates images from noise.",
    businessApplication: "Enables creation of product images, advertising materials, and illustrations. Dramatically reduces design costs."
  },
  {
    id: 32,
    term: "Text-to-Image",
    category: "Application",
    definition: "Technology that automatically generates images from text descriptions. Implemented in tools like Midjourney, DALL-E, and Stable Diffusion.",
    businessApplication: "Enables rapid creation of advertising images, product mockups, and social media content. Even non-designers can produce professional quality."
  },
  {
    id: 33,
    term: "Text-to-Video",
    category: "Application",
    definition: "Technology to automatically generate videos from text descriptions. Implemented in tools like Sora, Runway, and Kling.",
    businessApplication: "Enables low-cost creation of promotional videos, product explanation videos, and training content."
  },
  {
    id: 34,
    term: "Text-to-Speech (TTS)",
    category: "Application",
    definition: "Technology to convert text to natural-sounding speech. ElevenLabs and Voicevox are representative.",
    businessApplication: "Enables low-cost creation of narrations for e-learning content, announcements, and video guides."
  },
  {
    id: 35,
    term: "Copilot",
    category: "Application",
    definition: "AI assistant function built into Microsoft 365 (Word, Excel, Teams, etc.). Automates document creation, data analysis, and meeting summaries.",
    businessApplication: "Enables significant efficiency improvement in existing business workflows. Important for large enterprises introducing Microsoft environments."
  },
  {
    id: 36,
    term: "AI Code Generation",
    category: "Application",
    definition: "AI technology to automatically generate program code. GitHub Copilot and Claude Code are representative.",
    businessApplication: "Dramatically improves engineer productivity. Even non-engineers can create simple apps and automation scripts."
  },
  {
    id: 37,
    term: "No-code / Low-code AI",
    category: "Application",
    definition: "Tools that enable AI implementation and automation without programming knowledge. Bubble, Make (Integromat), and Zapier are representative.",
    businessApplication: "Enables business people themselves to build AI-powered business applications and automation workflows."
  },
  {
    id: 38,
    term: "Chatbot",
    category: "Application",
    definition: "A system that automatically responds to customer questions. AI-powered chatbots are spreading to natural conversation.",
    businessApplication: "24/7 unmanned customer support, FAQ automation, lead acquisition. Enables significant reduction in support costs."
  },
  {
    id: 39,
    term: "AI OCR",
    category: "Application",
    definition: "Character recognition technology that uses AI to read and digitize characters from images and PDFs with high accuracy.",
    businessApplication: "Enables automatic processing of invoices, contracts, and application forms. Dramatically reduces manual data entry work."
  },
  {
    id: 40,
    term: "Semantic Search",
    category: "Application",
    definition: "A search technology that understands the meaning of text, not just keyword matching, to find documents with similar content.",
    businessApplication: "Enables employees to quickly find relevant documents even without knowing the exact keywords. Dramatically improves knowledge management."
  },
  {
    id: 41,
    term: "Sentiment Analysis",
    category: "Application",
    definition: "AI technology to automatically classify customer reviews and social media posts as positive, neutral, or negative.",
    businessApplication: "Enables real-time analysis of customer satisfaction, brand reputation management, and product improvement priorities."
  },
  {
    id: 42,
    term: "AI Translation",
    category: "Application",
    definition: "Machine translation using AI, with tools like DeepL and Google Translate enabling high-quality translation.",
    businessApplication: "Enables rapid multilingual correspondence for global expansion. Enables low-cost translation of customer support, contracts, and product descriptions."
  },
  {
    id: 43,
    term: "AI Summarization",
    category: "Application",
    definition: "Technology that uses AI to automatically summarize long texts, meeting minutes, and documents to the key points.",
    businessApplication: "Enables significant reduction in time reading long reports and documents. Possible to process dozens of times more information in the same time."
  },
  {
    id: 44,
    term: "AI Recommendation",
    category: "Application",
    definition: "A system that analyzes user behavior history and preferences to suggest products, content, and services they might like.",
    businessApplication: "E-commerce product recommendations, streaming content recommendations, news feed personalization. Directly improves sales."
  },
  {
    id: 45,
    term: "Computer Vision",
    category: "Technology",
    definition: "AI technology that analyzes images and videos and understands what they show. Used in face recognition and defect detection.",
    businessApplication: "Used for quality inspection at manufacturing sites, security surveillance, retail customer behavior analysis, and medical diagnosis support."
  },
  {
    id: 46,
    term: "Speech Recognition (ASR)",
    category: "Technology",
    definition: "Technology that converts speech to text. Whisper (OpenAI) and Google Speech-to-Text are representative.",
    businessApplication: "Enables automatic generation of meeting minutes, real-time subtitles for customer support, and voice search functionality."
  },
  {
    id: 47,
    term: "Knowledge Graph",
    category: "Technology",
    definition: "A technique to represent relationships between information in a graph structure. Enables AI to understand complex business relationships and contexts.",
    businessApplication: "Used to understand relationships between corporate information, markets, and technologies. Supports sophisticated business intelligence systems."
  },
  {
    id: 48,
    term: "Model API",
    category: "Infrastructure",
    definition: "An interface that enables the use of AI model capabilities from external programs. OpenAI API, Anthropic API, and Google AI API are representative.",
    businessApplication: "Foundation for integrating AI functionality into existing business systems and apps. Enables building custom AI solutions."
  },
  {
    id: 49,
    term: "Prompt Injection",
    category: "Concept",
    definition: "A cyberattack that alters AI behavior through malicious instructions embedded in prompts. An important security risk in AI deployment.",
    businessApplication: "Security risk to be noted when deploying AI in customer-facing services. Security design considering attack scenarios is essential."
  },
  {
    id: 50,
    term: "AI Bias",
    category: "Concept",
    definition: "Unfair tendencies that appear in AI judgments due to bias in training data. Leads to discrimination and unfair treatment.",
    businessApplication: "Risk for AI-based personnel evaluation and credit screening. Governance framework for regular bias monitoring is important."
  },
  {
    id: 51,
    term: "AI Act (EU)",
    category: "Regulation",
    definition: "The world's first comprehensive AI regulation established by the EU in 2024, regulating high-risk AI by use case.",
    businessApplication: "Mandatory compliance for companies with business in the EU. Need to understand impact on HR evaluation, credit scoring, and biometric systems."
  },
  {
    id: 52,
    term: "AI Safety",
    category: "Concept",
    definition: "Research and practice area to deploy AI systems safely and ensure alignment with human values.",
    businessApplication: "Prerequisite for responsible AI use in business. Building oversight mechanisms and transparent operation processes."
  },
  {
    id: 53,
    term: "Autonomous AI",
    category: "Application",
    definition: "AI systems that can plan and execute tasks over long periods without human intervention. Next-generation evolution of AI agents.",
    businessApplication: "Automation of complex multi-step business processes. Important for efficiency improvement in repetitive complex workflows."
  },
  {
    id: 54,
    term: "Multimodal Learning",
    category: "Technology",
    definition: "Technology that enables AI to understand and generate content across multiple modalities: text, images, audio, and video.",
    businessApplication: "Enables sophisticated document understanding across text, charts, and images. Enables more sophisticated analysis and content generation."
  },
  {
    id: 55,
    term: "AI Infrastructure",
    category: "Infrastructure",
    definition: "All hardware, software, and cloud services required to build and operate AI systems. GPUs, cloud computing, and vector databases are included.",
    businessApplication: "Foundation for AI deployment. Optimal infrastructure selection based on scale, cost, and performance requirements."
  },
  {
    id: 56,
    term: "Explainable AI (XAI)",
    category: "Technology",
    definition: "Technology to clarify AI decision-making processes in a way that is understandable by humans. Increases transparency in AI judgments.",
    businessApplication: "Necessary for AI systems requiring explanation of grounds in medical diagnosis, financial decisions, and legal fields."
  },
  // ── Practical terms for working with AI agents ──────────────────
  {
    id: 57,
    term: "Path",
    category: "Agent",
    definition: "A string indicating the location of files or directories an AI agent reads and writes. Restricting which paths an agent may touch via an allowlist is a fundamental of agent operation.",
    businessApplication: "Limiting an agent's working scope by path prevents unintended file changes and information leakage. The first step toward safe automation."
  },
  {
    id: 58,
    term: "Domain",
    category: "Agent",
    definition: "Used in two senses: the 'target business area' and the 'network identifier (e.g., example.com)'. In agent operation, the domains an agent may access are restricted to control external communication.",
    businessApplication: "Allowlisting destination domains curbs the risk of confidential data being sent externally. Splitting specialized agents by business domain is also effective."
  },
  {
    id: 59,
    term: "Layer",
    category: "Agent",
    definition: "Each tier of a system organized by responsibility (e.g., UI layer, logic layer, data layer). Clarifying which layer to touch when asking AI for changes improves safety.",
    businessApplication: "Layer-aware instructions prevent an AI change from rippling into other features. A well-separated design also reduces maintenance cost."
  },
  {
    id: 60,
    term: "Sub-agent",
    category: "Agent",
    definition: "A subordinate agent to which the main AI agent delegates a specific task. Research, implementation, and review can be handed to specialized sub-agents in parallel.",
    businessApplication: "Delegating heavy work to sub-agents lets the main agent focus on orchestration. Parallel processing shortens the time for large tasks."
  },
  {
    id: 61,
    term: "Orchestration",
    category: "Agent",
    definition: "Centrally controlling the execution order and coordination of multiple AI agents and tools. The role of a 'conductor' that decides who runs what and when.",
    businessApplication: "Automates complex workflows through the cooperation of multiple agents, reducing manual progress management and enabling large-scale automation."
  },
  {
    id: 62,
    term: "Worktree",
    category: "Agent",
    definition: "A mechanism to carve multiple working directories out of a single Git repository. The foundation for multiple AI agents to work in parallel without interfering with each other.",
    businessApplication: "Because multiple agents can develop different features simultaneously without conflict, parallel-development throughput improves."
  },
];

const categoryColors: Record<string, { bg: string; border: string; text: string }> = {
  // Japanese categories
  "技術":    { bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.4)",  text: "#06b6d4" },
  "ツール名": { bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.4)", text: "#a78bfa" },
  "用語":    { bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.35)", text: "#67e8f9" },
  "概念":    { bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.35)", text: "#c4b5fd" },
  "エージェント": { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)", text: "#34d399" },
  // English categories
  "Technology":     { bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.4)",   text: "#06b6d4" },
  "Concept":        { bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.35)", text: "#c4b5fd" },
  "Practice":       { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)",  text: "#34d399" },
  "Application":    { bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.4)",  text: "#a78bfa" },
  "Infrastructure": { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.4)",  text: "#60a5fa" },
  "Regulation":     { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)",  text: "#fbbf24" },
  "Agent":          { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)",  text: "#34d399" },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

export default function AIGlossaryPage() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === 'ja' || i18n.language.startsWith('ja');
  const aiTerms = isJa ? aiTermsJa : aiTermsEn;
  const allCategoriesLabel = isJa ? "すべて" : "All";
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(allCategoriesLabel);

  const categories = useMemo(() => {
    const cats = [...new Set(aiTerms.map(term => term.category))];
    return [allCategoriesLabel, ...cats];
  }, [aiTerms, allCategoriesLabel]);

  const filteredTerms = useMemo(() => {
    return aiTerms.filter(term => {
      const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           term.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === allCategoriesLabel || term.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory, aiTerms, allCategoriesLabel]);

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: '#0a0e1a', color: 'white' }}>
      {/* 背景装飾 */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(6,182,212,0.04)' }}></div>
        <div className="absolute bottom-40 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(124,58,237,0.04)' }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <Link to="/">
          <button
            className="mb-6 inline-flex items-center px-4 py-2 rounded-full transition-all duration-300 font-medium"
            style={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.8)',
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('ai_glossary.back_home')}
          </button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('ai_glossary.title')}
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {t('ai_glossary.subtitle')}
          </p>
        </motion.div>

        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4" style={{ color: 'rgba(255,255,255,0.4)' }} />
            <Input
              placeholder={t('ai_glossary.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
              }}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const isSelected = selectedCategory === category;
              const displayLabel = category === allCategoriesLabel ? t('ai_glossary.all_categories') : category;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className="px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: isSelected ? 'rgba(6,182,212,0.2)' : 'rgba(255,255,255,0.06)',
                    border: isSelected ? '1px solid rgba(6,182,212,0.5)' : '1px solid rgba(255,255,255,0.15)',
                    color: isSelected ? '#06b6d4' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                  }}
                >
                  {displayLabel}
                </button>
              );
            })}
          </div>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          key={`${searchTerm}-${selectedCategory}`}
        >
          {filteredTerms.map((termData) => {
            const catColor = categoryColors[termData.category] ?? { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.2)", text: "rgba(255,255,255,0.7)" };
            return (
              <motion.div
                key={termData.id}
                variants={cardVariants}
                whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                transition={{ duration: 0.2 }}
                className="rounded-xl p-0 overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {/* Card Header */}
                <div className="px-5 pt-5 pb-3">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold" style={{ color: 'white' }}>{termData.term}</h3>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{
                        backgroundColor: catColor.bg,
                        border: `1px solid ${catColor.border}`,
                        color: catColor.text,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {termData.category}
                    </span>
                  </div>
                </div>
                {/* Card Content */}
                <div className="px-5 pb-5 space-y-4">
                  <div>
                    <h4 className="font-semibold text-sm mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_glossary.definition_label')}</h4>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      {termData.definition}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-2" style={{ color: '#06b6d4' }}>{t('ai_glossary.business_use_label')}</h4>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {termData.businessApplication}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {filteredTerms.length === 0 && (
          <div className="text-center py-12">
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_glossary.no_results')}</p>
            <p className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('ai_glossary.no_results_hint')}</p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="rounded-xl p-8 text-center"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderTop: '3px solid #7c3aed',
          }}
        >
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'white' }}>{t('ai_glossary.cta_title')}</h2>
          <p className="mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('ai_glossary.cta_body')}
          </p>
          <Link to="/#contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-3 rounded-full font-bold text-white transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
                boxShadow: '0 0 20px rgba(6,182,212,0.3)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {t('ai_glossary.cta_button')}
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
